/**
 * PWA install (6 Sep 2026). Chrome/Edge on Android and desktop fire `beforeinstallprompt` once the app qualifies (manifest,
 * icons, service worker, HTTPS — all in place); we hold that event and open the browser's own install dialog from a button.
 * iOS Safari has no such event: the card shows the two manual steps instead. Nothing shows once the app runs installed
 * (standalone), and "Ahora no" hides the card on that device.
 */
import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event { prompt(): Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> }
let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());
// captured at module load — the event can fire before React mounts
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); deferred = e as BeforeInstallPromptEvent; notify(); });
  window.addEventListener('appinstalled', () => { deferred = null; notify(); });
}
const DISMISS_KEY = 'gastos.install.dismissed';
const read = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const write = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } };

export const isStandalone = () => (typeof matchMedia !== 'undefined' && matchMedia('(display-mode: standalone)').matches) || (navigator as { standalone?: boolean }).standalone === true;
export const isIos = () => /iPhone|iPad|iPod/.test(navigator.userAgent) && !('MSStream' in window);

export interface InstallState { show: boolean; canPrompt: boolean; ios: boolean; install: () => Promise<void>; dismiss: () => void }
export function useInstall(): InstallState {
  const [, tick] = useState(0);
  useEffect(() => { const l = () => tick((n) => n + 1); listeners.add(l); return () => { listeners.delete(l); }; }, []);
  const dismissed = read(DISMISS_KEY) === '1';
  const canPrompt = deferred !== null; const ios = isIos();
  const show = !isStandalone() && !dismissed && (canPrompt || ios);
  return {
    show, canPrompt, ios,
    install: async () => { const e = deferred; if (!e) return; await e.prompt(); const { outcome } = await e.userChoice; if (outcome === 'accepted') deferred = null; notify(); },
    dismiss: () => { write(DISMISS_KEY, '1'); notify(); },
  };
}
